import { useGeolocation } from '@uidotdev/usehooks';
import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaListUl, FaMapMarkerAlt, FaSearch } from 'react-icons/fa';
import { LocationType } from '../../@posturinn/client-react';
import { removeSuffix } from '../../helpers/stringHelpers/stringHelpers';
import { getQueryParam } from '../../helpers/url/urlHelpers';
import useLocations from '../../hooks/useLocations';
import IOpeningHours, { ILocationImage } from '../../interfaces/openingHours/openingHours';
import { Column } from '../base/grid';
import { Tabs } from '../base/tabs/tabs';
import LocationCard from '../cards/locationCard';
import * as StyledLocationCard from '../cards/locationCard/locationCard.styles';
import LetterPostboxes from '../generalPostInfo/letterPostboxes';
import { LocationsMapContainer } from '../map/mapContainer';
import * as StyledInfoList from './infoList.styles';

interface Props {
	page: IOpeningHours;
}

const InfoList: FC<Props> = ({ page }) => {
	const RVK_LAT = 64.1256369;
	const RVK_LNG = -21.8756546;
	const ICELAND_CENTER_LAT = 64.9386083;
	const ICELAND_CENTER_LNG = -18.8422143;
	const { latitude, longitude } = useGeolocation();
	const [userLatitude, setUserLatitude]: [number, any] = useState(latitude || RVK_LAT);
	const [userLongitude, setUserLongitude]: [number, any] = useState(longitude || RVK_LNG);
	const [zoom, setZoom]: [number, any] = useState(12);
	const [activeTab, setActiveTab] = useState(4);
	const [queryStringRead, setQueryStringRead]: [boolean, any] = useState(false);
	const [activeMobileView, setActiveMobileView]: ['list' | 'map', any] = useState('list');
	const [searchQuery, setSearchQuery] = useState('');
	const { t } = useTranslation('openingHours');
	const {
		LocationImages: locationImages,
		FallbackPostboxImage,
		FallbackPostofficeImage,
		FallbackParcelpointImage,
	} = page.Values;
	const { locations } = useLocations();
	// Card columns by location id, so a click on a map marker can scroll the list to its card
	const cardRefs = useRef(new Map<string, HTMLDivElement>());
	const [highlightedLocationId, setHighlightedLocationId] = useState<string | null>(null);

	const locationImageMap = useMemo(
		() => new Map(locationImages.map(img => [img.Values.LocationID, img])),
		[locationImages]
	);

	const fallbackImages = {
		[LocationType.PostBox]: FallbackPostboxImage?.Src,
		[LocationType.PostOffice]: FallbackPostofficeImage?.Src,
		[LocationType.ParcelPoint]: FallbackParcelpointImage?.Src,
	};

	const getLocationByType = () => {
		const filteredLocations = [];
		let type = LocationType.PostOffice;
		if (activeTab === 0) type = LocationType.PostOffice;
		if (activeTab === 1) type = LocationType.PostBox;
		if (activeTab === 2) type = LocationType.ParcelPoint;

		if (locations) {
			let tmpLocationImage: ILocationImage | false = false; // Map.get(key) returns false if key is not found
			locations.forEach(location => {
				if (location.locationType === type || activeTab === 4) {
					tmpLocationImage = locationImageMap.get(location.id.replace('IS', '')); // location.id is a number
					filteredLocations.push({
						...location,
						imageURL: tmpLocationImage ? tmpLocationImage.Values?.LocationImage?.Src : false,
					});
				}
			});
		}
		return filteredLocations;
	};

	const onTabClick = tab => {
		setActiveTab(tab);
		setHighlightedLocationId(null);
		setUserLatitude(RVK_LAT);
		setUserLongitude(RVK_LNG);
		setZoom(12);
	};

	const onChange = e => {
		setSearchQuery(e.target.value);
		setZoom(7);
		setUserLatitude(ICELAND_CENTER_LAT);
		setUserLongitude(ICELAND_CENTER_LNG);
	};

	if (getQueryParam('tab') && Number(getQueryParam('tab')) !== activeTab && !queryStringRead) {
		setActiveTab(Number(getQueryParam('tab')));
		setQueryStringRead(true);
	}
	const onMobileViewToggleClick = () => {
		const nextMobileView = activeMobileView === 'list' ? 'map' : 'list';

		if (nextMobileView === 'map') {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}

		setActiveMobileView(nextMobileView);
	};

	const search = location => {
		return (
			searchQuery.length === 0 ||
			location.name.toUpperCase().includes(searchQuery.toUpperCase()) ||
			location.address.postalCode.toUpperCase().includes(searchQuery.toUpperCase()) ||
			location.address.street1.toUpperCase().includes(searchQuery.toUpperCase()) ||
			location.address.city.toUpperCase().includes(searchQuery.toUpperCase())
		);
	};

	const toFiniteNumber = value => {
		const num = Number(value);
		return Number.isFinite(num) ? num : null;
	};

	const onMarkerClick = (itemOrId, lat?: number, lng?: number, scroll?: boolean) => {
		// Handles clicks from both the list (location object) and map markers (id + coordinates)
		let selectedLocation = null;

		if (itemOrId && typeof itemOrId === 'object') {
			selectedLocation = itemOrId;
		}

		const resolvedLat = selectedLocation ? toFiniteNumber(selectedLocation.latitude) : null;
		const resolvedLng = selectedLocation ? toFiniteNumber(selectedLocation.longitude) : null;

		if (resolvedLat !== null && resolvedLng !== null) {
			setUserLatitude(resolvedLat);
			setUserLongitude(resolvedLng);
		} else if (typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng)) {
			setUserLatitude(lat);
			setUserLongitude(lng);
		}
		setZoom(15);

		const locationId = selectedLocation ? selectedLocation.id : itemOrId;
		if (typeof locationId === 'string') setHighlightedLocationId(locationId);

		if (scroll && typeof itemOrId === 'string') {
			cardRefs.current.get(itemOrId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	};

	return (
		<StyledInfoList.Container>
			<StyledInfoList.MobileViewToggle onClick={onMobileViewToggleClick}>
				{activeMobileView === 'list' ? (
					<StyledInfoList.MobileViewToggleContent>
						<FaMapMarkerAlt />
						<StyledInfoList.MobileViewToggleText>{`${t('see-map')}`}</StyledInfoList.MobileViewToggleText>
					</StyledInfoList.MobileViewToggleContent>
				) : (
					<StyledInfoList.MobileViewToggleContent>
						<FaListUl />
						<StyledInfoList.MobileViewToggleText>{`${t('see-list')}`}</StyledInfoList.MobileViewToggleText>
					</StyledInfoList.MobileViewToggleContent>
				)}
			</StyledInfoList.MobileViewToggle>
			<Tabs>
				<StyledInfoList.ContentTab active={activeTab === 4} onClick={onTabClick.bind(null, 4)}>
					{t('seeAll')}
				</StyledInfoList.ContentTab>
				<StyledInfoList.ContentTab active={activeTab === 0} onClick={onTabClick.bind(null, 0)}>
					{`${t('postoffice')}`}
				</StyledInfoList.ContentTab>
				<StyledInfoList.ContentTab active={activeTab === 1} onClick={onTabClick.bind(null, 1)}>
					{`${t('postbox')}`}
				</StyledInfoList.ContentTab>
				<StyledInfoList.ContentTab active={activeTab === 2} onClick={onTabClick.bind(null, 2)}>
					{`${t('parcelpoint')}`}
				</StyledInfoList.ContentTab>
				<StyledInfoList.ContentTab active={activeTab === 5} onClick={onTabClick.bind(null, 5)}>
					{`${t('letter-postboxes')}`}
				</StyledInfoList.ContentTab>
			</Tabs>
			{activeTab === 5 ? (
				<LetterPostboxes active />
			) : (
				<>
					<StyledInfoList.ViewColumns>
						<StyledInfoList.InfoListColumn
							desktopSmall={6 / 12}
							$activeMobileView={activeMobileView}
							$mobileView="list">
							<StyledInfoList.ListWrapper>
								<StyledInfoList.InputWrapper>
									<StyledInfoList.SearchInputWrapper>
										<StyledInfoList.SearchInput
											type="text"
											value={searchQuery}
											onChange={onChange}
											placeholder={`${t('search')}`}
										/>
										<FaSearch />
									</StyledInfoList.SearchInputWrapper>
								</StyledInfoList.InputWrapper>
								<StyledLocationCard.Scrollable flexWrap="wrap">
									{getLocationByType().map(
										location =>
											search(location) && (
												<Column
													tablet={1 / 2}
													key={location.id}
													ref={el => {
														if (el) cardRefs.current.set(location.id, el);
														else cardRefs.current.delete(location.id);
													}}
													marginBottom="3rem"
													data-locationid={removeSuffix(location.id, 'IS')}>
													<LocationCard
														location={location}
														setSelectedLocation={onMarkerClick}
														fallbackImages={fallbackImages}
														highlighted={location.id === highlightedLocationId}
													/>
												</Column>
											)
									)}
								</StyledLocationCard.Scrollable>
							</StyledInfoList.ListWrapper>
						</StyledInfoList.InfoListColumn>
						<StyledInfoList.InfoListColumn
							desktopSmall={6 / 12}
							$activeMobileView={activeMobileView}
							$mobileView="map">
							<StyledInfoList.MapWrapper>
								<LocationsMapContainer
									lat={userLatitude}
									lng={userLongitude}
									locations={getLocationByType()}
									zoom={zoom}
									onMarkerClick={onMarkerClick}
									search={search}
								/>
							</StyledInfoList.MapWrapper>
						</StyledInfoList.InfoListColumn>
					</StyledInfoList.ViewColumns>
				</>
			)}
		</StyledInfoList.Container>
	);
};

export default InfoList;
